import type { PaletteEntry } from '@designdeck/shared';
import apiClient, { ApiResponse } from './client';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

export interface EmbroideryImageResponse {
  /** Posterized preview placed on the canvas until a mapping is confirmed. */
  previewUrl: string;
  /** Storage key of the stored source, used to re-analyze after a reload. */
  embFileKey: string;
  originalName: string;
  /** Dominant design colors, at most 12, each with a suggested thread. */
  palette: PaletteEntry[];
}

// ═══════════════════════════════════════════════════════════════════
// API FUNCTIONS
// ═══════════════════════════════════════════════════════════════════

export async function uploadEmbroideryImage(
  file: File,
  onProgress?: (percent: number) => void
): Promise<EmbroideryImageResponse> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post<ApiResponse<EmbroideryImageResponse>>(
    '/embroidery/image',
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60_000,
      onUploadProgress: (event) => {
        if (onProgress && event.total) {
          onProgress(Math.round((event.loaded * 100) / event.total));
        }
      },
    }
  );

  return response.data.data;
}

/**
 * Palette for a motif already in storage.
 *
 * Needed when the thread modal is reopened in a later session: the canvas object
 * survives, the uploaded file does not, and the mapping's keys are design hexes
 * that need a palette to be shown against. The extraction is deterministic for a
 * given source, so the keys line up with what an existing mapping was recorded
 * against.
 */
export async function analyzeEmbroiderySource(
  embFileKey: string
): Promise<PaletteEntry[]> {
  const response = await apiClient.post<ApiResponse<PaletteEntry[]>>(
    '/embroidery/analyze',
    { embFileKey },
    { timeout: 60_000 }
  );

  return response.data.data;
}
